/**
 * 📊 DATA RICHNESS HELPERS
 * 
 * Conditional logic for displaying rich workout data based on availability.
 * Determines which progressive disclosure components to show for each workout.
 * 
 * Created: January 2026
 * Sprint 5: Frontend Data Richness
 */

import { UnifiedSessionData } from '../services/sessionDataAdapter';
import { getSourceCapabilities, canSourceProvide } from './sourceDataCapabilities';

/** Session source (dataSource from API or source from adapter). */
const getSource = (session: UnifiedSessionData): string =>
  (session.dataSource ?? session.source ?? '').toLowerCase();

/**
 * Check if workout has GPS route data for map display
 */
export const hasGPSData = (session: UnifiedSessionData): boolean => {
  return !!(
    session.gpsData && 
    session.gpsData.length > 0 &&
    session.gpsData.some(point => point.latitude !== undefined && point.longitude !== undefined)
  );
};

/**
 * Check if workout has elevation data for profile chart
 */
export const hasElevationData = (session: UnifiedSessionData): boolean => {
  return !!(
    session.elevationGain !== undefined || 
    session.elevationLoss !== undefined ||
    (session.gpsData && session.gpsData.some(point => point.altitude !== undefined))
  );
};

/**
 * Check if workout has heart rate zone distribution
 */
export const hasHeartRateZones = (session: UnifiedSessionData): boolean => {
  if (!session.hrZones || typeof session.hrZones !== 'object') return false;
  const zones = session.hrZones as Record<string, unknown>;
  return Object.keys(zones).some(k => zones[k] !== undefined && zones[k] !== null);
};

/**
 * Check if workout has heart rate data (avg/max)
 */
export const hasHeartRateData = (session: UnifiedSessionData): boolean => {
  return !!(
    session.avgHeartRate !== undefined || 
    session.maxHeartRate !== undefined ||
    hasHeartRateZones(session)
  );
};

/**
 * Check if workout has power data
 */
export const hasPowerData = (session: UnifiedSessionData): boolean => {
  return !!(
    session.avgPower !== undefined || 
    session.maxPower !== undefined ||
    session.normalizedPower !== undefined ||
    (session.powerZones && typeof session.powerZones === 'object' && Object.keys(session.powerZones).length > 0)
  );
};

/**
 * Check if workout has cadence data
 */
export const hasCadenceData = (session: UnifiedSessionData): boolean => {
  return !!(
    session.avgCadence !== undefined || 
    session.maxCadence !== undefined
  );
};

/**
 * Check if workout has power OR cadence data (for combined component)
 */
export const hasPowerOrCadenceData = (session: UnifiedSessionData): boolean => {
  return hasPowerData(session) || hasCadenceData(session);
};

/**
 * Check if workout has Strava segments
 */
export const hasSegments = (session: UnifiedSessionData): boolean => {
  return !!(
    session.segments && 
    session.segments.length > 0
  );
};

/**
 * Check if workout has structured phases (intervals, HIIT, etc.)
 */
export const hasPhases = (session: UnifiedSessionData): boolean => {
  return !!(
    session.phases && 
    session.phases.length > 1  // More than 1 phase = structured workout
  );
};

/**
 * Check if workout has split/lap data
 */
export const hasSplits = (session: UnifiedSessionData): boolean => {
  return !!(
    session.splits && 
    session.splits.length > 0
  );
};

/**
 * Check if workout has exercise details (strength training)
 */
export const hasExercises = (session: UnifiedSessionData): boolean => {
  return !!(
    session.exercises && 
    session.exercises.length > 0
  );
};

/**
 * Check if workout has HIIT-specific data (circuits, EMOM, AMRAP)
 */
export const hasHIITData = (session: UnifiedSessionData): boolean => {
  const type = session.type?.toLowerCase() || '';
  const activityType = session.activityType?.toLowerCase() || '';
  
  return (
    type.includes('circuit') || 
    type.includes('emom') || 
    type.includes('amrap') ||
    type.includes('hiit') ||
    activityType.includes('circuit') ||
    activityType.includes('emom') ||
    activityType.includes('amrap') ||
    activityType.includes('hiit')
  );
};

/**
 * Check if workout has time-series data (trackpoints with HR/pace/cadence/power)
 * for the Activity Timeline chart
 */
export const hasTimeSeriesData = (session: UnifiedSessionData): boolean => {
  if (!session.samples || !Array.isArray(session.samples) || session.samples.length < 3) return false;
  // Check that at least some samples have a metric beyond just GPS
  return session.samples.some((s: any) =>
    s.heart_rate != null || s.heartRate != null || s.hr != null ||
    s.speed != null || s.speedMetersPerSecond != null ||
    s.cadence != null || s.runCadence != null ||
    s.power != null
  );
};

/**
 * Check if workout has Cedar Insights (post-workout analysis)
 */
export const hasCedarInsights = (session: UnifiedSessionData): boolean => {
  return !!(
    session.cedarInsights && 
    session.cedarInsights.length > 0
  );
};

/**
 * Check if workout has "How It Went" data (user feedback)
 */
export const hasHowItWent = (session: UnifiedSessionData): boolean => {
  return !!(
    session.userFeedback ||
    session.perceivedEffort !== undefined ||
    session.completionNotes ||
    hasCedarInsights(session)
  );
};

/**
 * Check if workout is from an external device (Garmin, Strava, iOS, Android)
 */
export const isExternalDevice = (session: UnifiedSessionData): boolean => {
  const source = getSource(session) || '';
  return (
    source === 'garmin' ||
    source === 'strava' ||
    source === 'ios_healthkit' ||
    source === 'apple_health' ||
    source === 'android_health_connect' ||
    source === 'android_health' ||
    source === 'google_health'
  );
};

/**
 * Check if workout is from Coach Cedar in-app tracker
 */
export const isInAppTracker = (session: UnifiedSessionData): boolean => {
  const source = getSource(session) || '';
  return source === 'cedar' || source === 'coach_cedar' || source === 'in_app';
};

/**
 * Check if workout has Garmin-specific metrics
 *
 * NOTE: vo2MaxEstimate was removed April 2026 (per-activity number was noisy and
 * confused users — see device-import parity mission). The currentVo2Max resolver
 * still surfaces a Garmin-sourced rolling value when available, but its presence
 * doesn't imply the workout itself was Garmin-recorded.
 */
export const hasGarminMetrics = (session: UnifiedSessionData): boolean => {
  return !!(
    session.trainingEffect !== undefined ||
    getSource(session) === 'garmin'
  );
};

/**
 * Check if workout has Strava-specific metrics
 */
export const hasStravaMetrics = (session: UnifiedSessionData): boolean => {
  return !!(
    session.sufferScore !== undefined ||
    session.stravaActivityId !== undefined ||
    hasSegments(session) ||
    getSource(session) === 'strava'
  );
};

/**
 * Determine data richness level for UI display decisions
 * 
 * Returns:
 * - 'basic': Minimal data (duration, distance only)
 * - 'standard': Basic + HR or pace data
 * - 'rich': Standard + GPS or phases
 * - 'elite': Rich + power/cadence/segments/detailed metrics
 */
export const getDataRichnessLevel = (session: UnifiedSessionData): 'basic' | 'standard' | 'rich' | 'elite' => {
  // Elite: Has power, cadence, segments, or time-series data
  if (hasPowerData(session) || hasCadenceData(session) || hasSegments(session) ||
      hasTimeSeriesData(session)) {
    return 'elite';
  }
  
  // Rich: Has GPS route or structured phases
  if (hasGPSData(session) || hasPhases(session)) {
    return 'rich';
  }
  
  // Standard: Has HR data or pace data
  if (hasHeartRateData(session) || session.avgPace !== undefined) {
    return 'standard';
  }
  
  // Basic: Only duration/distance
  return 'basic';
};

/**
 * Get list of expandable sections to display for this workout.
 *
 * Uses a two-gate system:
 *  1. Source capability gate — can this source even provide this data type?
 *  2. Data presence gate — does the data actually exist in the session?
 *
 * Both must pass for a section to appear. This prevents showing empty sections
 * for data types the source fundamentally can't provide (e.g., Activity Timeline
 * for a Garmin-via-HealthKit workout that only has summary HR).
 *
 * Returns array of section identifiers that should be rendered.
 */
export const getExpandableSections = (session: UnifiedSessionData): string[] => {
  const sections: string[] = [];
  const caps = getSourceCapabilities(
    session.dataSource ?? session.source,
    session.sourceAppName,
  );

  // Always show "How It Went" if data exists (not source-dependent)
  if (hasHowItWent(session)) {
    sections.push('how-it-went');
  }

  // Phase breakdown for structured workouts (not source-dependent)
  if (hasPhases(session)) {
    sections.push('phases');
  }

  // Splits for workouts with lap data
  if (hasSplits(session) && canSourceProvide('splits', caps)) {
    sections.push('splits');
  }

  // GPS route map
  if (hasGPSData(session) && canSourceProvide('gps-route', caps)) {
    sections.push('gps-route');
  }

  // Elevation profile
  if (hasElevationData(session) && canSourceProvide('elevation', caps)) {
    sections.push('elevation');
  }

  // Heart rate zones
  if (hasHeartRateZones(session) && canSourceProvide('hr-zones', caps)) {
    sections.push('hr-zones');
  }

  // Power and cadence
  if (hasPowerOrCadenceData(session) && canSourceProvide('power-cadence', caps)) {
    sections.push('power-cadence');
  }

  // Activity timeline (time-series multi-metric chart)
  if (hasTimeSeriesData(session) && canSourceProvide('activity-timeline', caps)) {
    sections.push('activity-timeline');
  }

  // Strava segments
  if (hasSegments(session) && canSourceProvide('segments', caps)) {
    sections.push('segments');
  }

  // Exercise details (strength training, not source-dependent)
  if (hasExercises(session)) {
    sections.push('exercises');
  }

  // HIIT breakdown (not source-dependent)
  if (hasHIITData(session)) {
    sections.push('hiit');
  }

  return sections;
};

/**
 * Get primary metrics to display in 3-column grid
 * 
 * Returns array of metric objects with label, value, and unit
 */
export const getPrimaryMetrics = (session: UnifiedSessionData): Array<{label: string, value: string, unit: string}> => {
  const metrics: Array<{label: string, value: string, unit: string}> = [];
  
  // Duration (always show if available; support both duration and totalDuration)
  const durationSec = session.duration ?? session.totalDuration;
  if (durationSec !== undefined) {
    const hours = Math.floor(durationSec / 3600);
    const minutes = Math.floor((durationSec % 3600) / 60);
    const seconds = durationSec % 60;
    
    let durationStr = '';
    if (hours > 0) {
      durationStr = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      durationStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    
    metrics.push({
      label: 'Duration',
      value: durationStr,
      unit: ''
    });
  }
  
  // Distance (for cardio activities)
  if (session.distance !== undefined && session.distance > 0) {
    metrics.push({
      label: 'Distance',
      value: (session.distance / 1000).toFixed(2),
      unit: 'km'
    });
  }
  
  // Pace (for running/cycling)
  if (session.avgPace !== undefined) {
    const paceMinutes = Math.floor(session.avgPace / 60);
    const paceSeconds = Math.floor(session.avgPace % 60);
    metrics.push({
      label: 'Avg Pace',
      value: `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')}`,
      unit: '/km'
    });
  }
  
  // Average Heart Rate
  if (session.avgHeartRate !== undefined) {
    metrics.push({
      label: 'Avg HR',
      value: Math.round(session.avgHeartRate).toString(),
      unit: 'bpm'
    });
  }
  
  // Average Power (for cycling/rowing)
  if (session.avgPower !== undefined) {
    metrics.push({
      label: 'Avg Power',
      value: Math.round(session.avgPower).toString(),
      unit: 'W'
    });
  }
  
  // Elevation Gain
  if (session.elevationGain !== undefined && session.elevationGain > 0) {
    metrics.push({
      label: 'Elevation',
      value: Math.round(session.elevationGain).toString(),
      unit: 'm'
    });
  }
  
  // Training Effect (Garmin-specific)
  if (session.trainingEffect !== undefined) {
    metrics.push({
      label: 'Training Effect',
      value: session.trainingEffect.toFixed(1),
      unit: ''
    });
  }
  
  // Current VO2 Max (resolver-derived, with source attribution)
  // Replaces per-activity vo2_max_estimate (April 2026 device-import parity).
  if (session.currentVo2Max?.value != null) {
    metrics.push({
      label: 'Current VO2 max',
      value: Math.round(session.currentVo2Max.value).toString(),
      unit: 'ml/kg/min'
    });
  }
  
  // Suffer Score (Strava-specific)
  if (session.sufferScore !== undefined) {
    metrics.push({
      label: 'Suffer Score',
      value: session.sufferScore.toString(),
      unit: ''
    });
  }
  
  // Return top 3 most relevant metrics
  return metrics.slice(0, 3);
};

/**
 * Get secondary metrics for detailed view
 * 
 * Returns array of additional metrics beyond the primary 3
 */
export const getSecondaryMetrics = (session: UnifiedSessionData): Array<{label: string, value: string, unit: string}> => {
  const allMetrics = getPrimaryMetrics(session);
  const primary = allMetrics.slice(0, 3);
  const secondary = allMetrics.slice(3);
  
  // Add max HR if not in primary
  if (session.maxHeartRate !== undefined && !primary.some(m => m.label === 'Max HR')) {
    secondary.push({
      label: 'Max HR',
      value: Math.round(session.maxHeartRate).toString(),
      unit: 'bpm'
    });
  }
  
  // Add max power if not in primary
  if (session.maxPower !== undefined && !primary.some(m => m.label === 'Max Power')) {
    secondary.push({
      label: 'Max Power',
      value: Math.round(session.maxPower).toString(),
      unit: 'W'
    });
  }
  
  // Add elevation loss if available
  if (session.elevationLoss !== undefined && session.elevationLoss > 0) {
    secondary.push({
      label: 'Elev Loss',
      value: Math.round(session.elevationLoss).toString(),
      unit: 'm'
    });
  }
  
  // Add cadence if available
  if (session.avgCadence !== undefined) {
    secondary.push({
      label: 'Avg Cadence',
      value: Math.round(session.avgCadence).toString(),
      unit: 'rpm'
    });
  }
  
  return secondary;
};

/**
 * Determine if workout should show "Data from [Device]" attribution
 */
export const shouldShowDeviceAttribution = (session: UnifiedSessionData): boolean => {
  return isExternalDevice(session) && !!(
    session.deviceModel || 
    session.deviceManufacturer ||
    (session.dataSource ?? session.source)
  );
};

/**
 * Get device attribution text
 */
export const getDeviceAttributionText = (session: UnifiedSessionData): string => {
  if (session.deviceModel && session.deviceManufacturer) {
    return `${session.deviceManufacturer} ${session.deviceModel}`;
  }
  
  if (session.deviceModel) {
    return session.deviceModel;
  }
  
  if (session.deviceManufacturer) {
    return session.deviceManufacturer;
  }
  
  const source = session.dataSource ?? session.source ?? '';
  if (source.toLowerCase() === 'garmin') {
    return 'Garmin Device';
  }
  if (source.toLowerCase() === 'strava') {
    return 'Strava';
  }
  if (source.toLowerCase() === 'ios_healthkit' || source.toLowerCase() === 'apple_health') {
    return 'Apple Health';
  }
  if (source.toLowerCase() === 'android_health_connect' || source.toLowerCase() === 'android_health') {
    return 'Google Health Connect';
  }
  if (source.toLowerCase() === 'google_health') {
    return 'Google Health';
  }

  return 'External Device';
};

/**
 * Concise device attribution for the shareable workout card, shown under the
 * date. Returns undefined for in-app (Cedar-timer) completions — only an
 * external recording device is credited. Unlike getDeviceAttributionText, this
 * is safe for a public share artifact:
 *   • collapses a doubled brand — the backend stores a bare "Apple" / "Garmin"
 *     manufacturer AND a brand-prefixed model, so a naïve `mfr + model` yields
 *     "Apple Apple Watch Ultra 2" / "Garmin Garmin Forerunner 945",
 *   • credits the real recording platform for Strava/Health pass-through via
 *     upstreamPlatform (Peloton / Zwift / Wahoo / Garmin) instead of a bare
 *     "Strava",
 *   • uses product names ("Health Connect", not "Google Health Connect") and
 *     omits generic "External Device" stand-ins.
 * Kept in lockstep with DataSourceBadges (the completed-workout card).
 */
const _UPSTREAM_BRAND: Record<string, string> = {
  peloton: 'Peloton',
  zwift: 'Zwift',
  wahoo: 'Wahoo',
  garmin: 'Garmin',
  garmin_connect: 'Garmin',
};

export const getShareCardDeviceLabel = (
  session: UnifiedSessionData,
): string | undefined => {
  if (!isExternalDevice(session)) return undefined;

  const model = (session.deviceModel || '').trim();
  const mfr = (session.deviceManufacturer || '').trim();

  // A concrete device model is the most specific, self-explanatory label.
  if (model) {
    // Prepend the manufacturer only when the model doesn't already carry the
    // brand — avoids "Apple Apple Watch …" and "Garmin Garmin Forerunner …".
    if (mfr && !model.toLowerCase().startsWith(mfr.toLowerCase())) {
      return `${mfr} ${model}`;
    }
    return model;
  }

  // Pass-through (Strava / Health) with no model — credit the real platform.
  const upstream = ((session as any).upstreamPlatform || '').toLowerCase();
  if (_UPSTREAM_BRAND[upstream]) return _UPSTREAM_BRAND[upstream];

  // Source-brand fallback, matching DataSourceBadges' product names.
  const source = (session.dataSource ?? session.source ?? '').toLowerCase();
  if (source === 'garmin') return 'Garmin';
  if (source === 'strava') return 'Strava';
  if (source === 'ios_healthkit' || source === 'apple_health') return 'Apple Watch';
  if (
    source === 'android_health_connect' ||
    source === 'android_health' ||
    source === 'health_connect'
  ) return 'Health Connect';
  if (source === 'google_health') return 'Google Health';

  // Unknown external source — no clean brand; omit rather than a stand-in.
  return undefined;
};

/**
 * Check if workout has minimal data (duration/distance only)
 */
export const hasMinimalData = (session: UnifiedSessionData): boolean => {
  return getDataRichnessLevel(session) === 'basic';
};

/**
 * Check if workout has elite-level data richness
 */
export const hasEliteData = (session: UnifiedSessionData): boolean => {
  return getDataRichnessLevel(session) === 'elite';
};

/**
 * Get data source badge information
 */
export const getDataSourceBadges = (session: UnifiedSessionData): Array<{
  source: string;
  icon: string;
  label: string;
}> => {
  const badges: Array<{source: string; icon: string; label: string}> = [];
  
  // Always show Coach Cedar first
  badges.push({
    source: 'coach_cedar',
    icon: '/favicon.png',
    label: 'Coach Cedar'
  });
  
  // Add Garmin if applicable
  if (getSource(session) === 'garmin' || hasGarminMetrics(session)) {
    badges.push({
      source: 'garmin',
      icon: '/garmin-assets/garmin-tag-black.png',
      label: session.deviceModel || 'Garmin'
    });
  }
  
  // Add Strava if applicable
  if (getSource(session) === 'strava' || hasStravaMetrics(session)) {
    badges.push({
      source: 'strava',
      icon: '/strava-assets/powered-by-strava-black.png',
      label: 'Strava'
    });
  }
  
  // Add iOS/Android if applicable
  if (getSource(session) === 'ios_healthkit') {
    badges.push({
      source: 'ios',
      icon: '/apple-health-icon.png',
      label: 'Apple Health'
    });
  }
  
  if (getSource(session) === 'android_health_connect') {
    badges.push({
      source: 'android',
      icon: '/google-health-icon.png',
      label: 'Health Connect'
    });
  }

  // Google Health (Pixel Watch / Fitbit cloud) — source peer; show the device
  // model when known, else "Google Health". (Reuses the existing google-health
  // icon asset; the official Google Health brand mark is a Phase-5 asset TODO.)
  if (getSource(session) === 'google_health') {
    badges.push({
      source: 'google_health',
      icon: '/google-health-icon.png',
      label: session.deviceModel || 'Google Health'
    });
  }

  return badges;
};

/**
 * Check if workout was pushed to a device (Garmin/Apple Watch)
 */
export const wasPushedToDevice = (session: UnifiedSessionData): boolean => {
  return !!(
    (session.watchPushStatus === 'pushed' || session.watchPushStatus === 'success') &&
    session.watchPlatform &&
    (session.watchPlatform === 'garmin' || session.watchPlatform === 'apple_watch')
  );
};

/**
 * Get watch platform information for pushed workouts
 */
export const getWatchPlatformInfo = (session: UnifiedSessionData): {
  platform: string;
  icon: string;
  label: string;
} | null => {
  if (!wasPushedToDevice(session)) {
    return null;
  }
  
  if (session.watchPlatform === 'garmin') {
    return {
      platform: 'garmin',
      icon: '/garmin-assets/garmin-tag-black.png',
      label: session.deviceModel || 'Garmin Watch'
    };
  }
  
  if (session.watchPlatform === 'apple_watch') {
    return {
      platform: 'apple_watch',
      icon: '/apple-watch-icon.png',
      label: session.deviceModel || 'Apple Watch'
    };
  }
  
  return null;
};

/**
 * Determine workout card layout based on data richness
 * 
 * Returns:
 * - 'minimal': Show basic info only (duration, distance)
 * - 'standard': Show primary metrics grid
 * - 'expanded': Show primary metrics + 2-3 expandable sections
 * - 'comprehensive': Show all available data with full progressive disclosure
 */
export const getRecommendedLayout = (session: UnifiedSessionData): 'minimal' | 'standard' | 'expanded' | 'comprehensive' => {
  const richness = getDataRichnessLevel(session);
  const sections = getExpandableSections(session);
  
  if (richness === 'elite' || sections.length >= 5) {
    return 'comprehensive';
  }
  
  if (richness === 'rich' || sections.length >= 3) {
    return 'expanded';
  }
  
  if (richness === 'standard' || sections.length >= 1) {
    return 'standard';
  }
  
  return 'minimal';
};

/**
 * Check if workout should show progressive disclosure UI
 */
export const shouldShowProgressiveDisclosure = (session: UnifiedSessionData): boolean => {
  const sections = getExpandableSections(session);
  return sections.length > 0;
};

/**
 * Get count of expandable sections available
 */
export const getExpandableSectionCount = (session: UnifiedSessionData): number => {
  return getExpandableSections(session).length;
};


